import { z } from 'zod';
import { HEALTH_REASONS, HEALTH_STATES, SERVICE_TYPE_LABELS } from '@bekebi/domain';
import {
  countdownSchema,
  httpUrlSchema,
  priceSchema,
  renewalConditionSchema,
  serviceTypeSchema,
  uuidSchema,
} from '../common';
import { pageSchema } from './pagination';

/**
 * The service layer: Cloud Server, Mail Server, Storage Bucket, Website. These responses are
 * readable with `services.view` (Admin and Operations), so they carry no infrastructure, provider,
 * binding or credential data — that lives only on `infrastructure.*` endpoints.
 */
const serviceNames = Object.values(SERVICE_TYPE_LABELS) as [string, ...string[]];

/** Current (open) renewal term; condition and countdown derived at request time (A-R3, A-R4). */
export const serviceRenewalSummarySchema = z.object({
  renewalDate: z.string(),
  price: priceSchema,
  condition: renewalConditionSchema,
  countdown: countdownSchema,
});
export type ServiceRenewalSummary = z.infer<typeof serviceRenewalSummarySchema>;

/**
 * Health is derived from real, current check results only (service-health.ts). Without current
 * service-level evidence a service is `unknown` — never invented. No check details (hosts, ports,
 * certificates) appear here; they are on the Admin-only monitoring endpoints.
 */
export const serviceHealthSchema = z.object({
  state: z.enum(HEALTH_STATES),
  reason: z.enum(HEALTH_REASONS),
  checkedAt: z.string().nullable(),
});

export const serviceSchema = z.object({
  id: uuidSchema,
  type: serviceTypeSchema,
  name: z.enum(serviceNames),
  /** The Website's public address (Website only; never a vendor control-panel link). */
  accessUrl: z.string().nullable(),
  health: serviceHealthSchema,
  /** Null when the service has no open renewal term. */
  renewal: serviceRenewalSummarySchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Service = z.infer<typeof serviceSchema>;

export const servicePageSchema = pageSchema(serviceSchema);

/** `services.manage` (Admin). `accessUrl` is accepted for Website services only. */
export const serviceCreateSchema = z.strictObject({
  type: serviceTypeSchema,
  accessUrl: httpUrlSchema.nullable().optional(),
});
export type ServiceCreate = z.infer<typeof serviceCreateSchema>;

export const serviceUpdateSchema = z.strictObject({
  accessUrl: httpUrlSchema.nullable(),
});
export type ServiceUpdate = z.infer<typeof serviceUpdateSchema>;

export const serviceParamsSchema = z.object({ serviceId: uuidSchema });
